import { useCallback, useEffect, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import ErrorBoundary from '@/components/error-boundary';
import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { ufoEventRedirectToProduct } from '@/components/signup/utils/ufo-events';
import { useProvisioningLatencyUfo } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-provisioning-latency-ufo';
import { trackPerceivedProvisioningEvent } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/utils/analytics';

import { useCloudStatus } from '../../hooks/use-cloud-status';
import { useCloudStatusRedirection } from '../../hooks/use-cloud-status-redirection';
import { useMatureExampleProject } from '../../hooks/use-create-sample-project';
import { useTenantAwareStatsigClientUpdater } from '../../hooks/use-tenant-aware-statsig-client-updater';
import { JiraAnimatedProvisioningScreen } from '../jira/jira-animated-provisioning-screen/jira-animated-provisioning-screen';

import { ANALYTICS_PREFIX } from './common/constants';
import { useEligibleForSampleProject } from './controllers';
import { useIsWizardVariation } from './hooks/use-is-wizard-variation';
import { useCreateRedirectUrlWithWizard } from './utils/create-redirect-url';
import { InviteStepCreateProjectTaskPollingProvider } from './wizard/invite-step/hooks/use-invite-step-create-project-task-polling-context';
import { WelcomeModal } from './wizard/welcome-modal';

export const OnboardingWizard = () => {
	// HOOKS
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { createCloudStatus, startPerceivedProvisioningTimer, perceivedProvisioningStartTime } =
		useCloudStatus();
	const { setWaitingForProvisioning, resolveBlockingRedirectPromises, createRedirectUrl } =
		useCloudStatusRedirection();
	const { completeAll } = useProvisioningLatencyUfo();
	const { products, signupId } = useSearchParameters();
	const { userEligibleForSampleProject } = useEligibleForSampleProject();
	const { updatedStatsigClientWithCloudId } = useTenantAwareStatsigClientUpdater();
	const { isWizardVariation } = useIsWizardVariation({
		updatedStatsigClientWithCloudId,
	});
	const createRedirectUrlWithWizard = useCreateRedirectUrlWithWizard();

	// STATE
	const productKey = products.length > 0 ? products[0] : undefined;
	const provisioningEventFired = useRef(false);
	const [siteProvisionedOnMount] = useState(createCloudStatus === 'success');

	useMatureExampleProject(userEligibleForSampleProject);

	useEffect(() => {
		setWaitingForProvisioning(true);
	}, [setWaitingForProvisioning]);

	useEffect(() => {
		startPerceivedProvisioningTimer();
	}, [startPerceivedProvisioningTimer]);

	const trackProvisioningEvent = () => {
		if (!provisioningEventFired.current) {
			trackPerceivedProvisioningEvent({
				createAnalyticsEvent,
				productKey,
				finalProvisioningScreen: 'jira-onboarding-wizard',
				startTimeSeconds: perceivedProvisioningStartTime,
				signupId,
			});
			provisioningEventFired.current = true;
		}
	};

	const onComplete = async (taskId?: string, onRedirect?: () => void) => {
		const productUrl = createRedirectUrl();

		if (productUrl) {
			await resolveBlockingRedirectPromises();

			if (onRedirect) {
				try {
					onRedirect();
				} catch {
					// NO-OP, prevent redirect from failing
				}
			}

			createAnalyticsEvent({
				eventName: 'redirectedToProduct',
				eventComponent: 'confirmationPage',
				event: 'redirected',
				eventType: 'ui',
				action: 'redirected',
				attributes: {
					reactComponent: 'OnboardingWizard',
				},
			}).fire();

			// SLO "good event" for redirect into product
			ufoEventRedirectToProduct.success({
				customData: {
					analyticsEvent: 'redirected',
					redirectFrom: 'confirmationPage',
					reactComponent: 'OnboardingWizard',
					finalProvisioningScreen: 'jira-onboarding-wizard',
				},
			});

			completeAll('jira-onboarding-wizard');
			window.location.assign(createRedirectUrlWithWizard(productUrl, taskId));
		}
	};

	const handleOnError = useCallback(
		(error: Error) => {
			createAnalyticsEvent({
				eventType: 'operational',
				actionSubject: `${ANALYTICS_PREFIX}Screen`,
				action: 'failed',
				attributes: {
					error: error.message,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	// This acts like ProductRedirector to avoid showing JiraAnimatedProvisioningScreen if site is ready
	if (siteProvisionedOnMount && !isWizardVariation) {
		return null;
	}

	if (isWizardVariation) {
		trackProvisioningEvent();
	}

	// Handles both cases where site is still provisioning and we show wizard
	// or show wizard immediately when site provisioned on mount
	if (isWizardVariation) {
		return (
			<ErrorBoundary fallback={<JiraAnimatedProvisioningScreen />} onError={handleOnError}>
				<InviteStepCreateProjectTaskPollingProvider>
					<WelcomeModal onComplete={onComplete} />
				</InviteStepCreateProjectTaskPollingProvider>
			</ErrorBoundary>
		);
	}

	return <JiraAnimatedProvisioningScreen />;
};
